import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Tooltip,
  TablePagination,
  FormControlLabel,
  Switch,
  InputAdornment,
  Divider,
  Alert,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  ShoppingCart as SalesIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import Designer from '../Designer/Designer';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { PAYMENT_STATUSES, DELIVERY_STATUSES } from '../../constants/salesConstants';

const ORDER_STATUS = {
  NEW: { value: 'new', label: 'Nowe', color: 'info' },
  IN_PROGRESS: { value: 'in_progress', label: 'W realizacji', color: 'warning' },
  READY: { value: 'ready', label: 'Gotowe', color: 'success' },
  COMPLETED: { value: 'completed', label: 'Zakończone', color: 'success' },
  CANCELLED: { value: 'cancelled', label: 'Anulowane', color: 'error' }
};

const Orders = () => {
  const { currentUser } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = currentUser?.role === 'admin';

  const [orders, setOrders] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [designerOpen, setDesignerOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    product_description: '',
    estimated_completion: '',
    deposit_amount: '',
    has_deposit: false,
    notes: '',
    status: 'new',
    total_amount: '',
    remaining_amount: '',
    is_fully_paid: false,
    is_delivered: false,
    create_sale_record: false,
    employee_id: ''
  });

  const handleFormChange = (field, value) => {
    setFormData(prevData => {
      const newData = { ...prevData, [field]: value };
      if (field === 'has_deposit' && !value) {
        newData.deposit_amount = '';
      }
      const total = parseFloat(newData.total_amount) || 0;
      const deposit = newData.has_deposit ? (parseFloat(newData.deposit_amount) || 0) : 0;
      newData.remaining_amount = calculateRemainingAmount(total, deposit);
      newData.has_deposit = !!newData.has_deposit;
      newData.is_fully_paid = !!newData.is_fully_paid;
      newData.is_delivered = !!newData.is_delivered;
      newData.create_sale_record = !!newData.create_sale_record;
      return newData;
    });
  };

  const handleClientChange = (event, newValue) => {
    setSelectedClient(newValue);
    setFormData(prevData => ({
      ...prevData,
      client_name: newValue ? newValue.name : '',
      client_phone: newValue ? newValue.phone : '',
    }));
  };

  const fetchOrders = useCallback(async () => {
    if (!currentUser) return;
    try {
      const ordersCollection = collection(db, 'orders');
      const queryConstraints = [orderBy('created_at', 'desc')];
      if (isAdmin && selectedEmployee) {
        queryConstraints.unshift(where('employee_id', '==', selectedEmployee));
      } else if (!isAdmin) {
        queryConstraints.unshift(where('employee_id', '==', currentUser.uid));
      }
      const q = query(ordersCollection, ...queryConstraints);
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate(),
        estimated_completion: doc.data().estimated_completion?.toDate(),
      }));
      setOrders(ordersData);
    } catch (error) {
      console.error("Błąd podczas pobierania zamówień:", error);
      enqueueSnackbar('Nie udało się załadować zamówień.', { variant: 'error' });
    }
  }, [currentUser, isAdmin, selectedEmployee, enqueueSnackbar]);

  const fetchEmployees = useCallback(async () => {
    if (!currentUser || currentUser.role !== 'admin') return;
    try {
      const usersCollection = collection(db, 'users');
      const q = query(usersCollection, orderBy('name'));
      const querySnapshot = await getDocs(q);
      const employeesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEmployees(employeesData);
    } catch (error) {
      console.error("Błąd podczas pobierania pracowników:", error);
      enqueueSnackbar('Nie udało się załadować listy pracowników.', { variant: 'error' });
    }
  }, [isAdmin, enqueueSnackbar]);

  const fetchClients = useCallback(async () => {
    if (!currentUser) return;
    try {
      const clientsCollection = collection(db, 'clients');
      let clientsQuery;
      if (isAdmin) {
        clientsQuery = query(clientsCollection, orderBy('name'));
      } else {
        clientsQuery = query(clientsCollection, where('assigned_to', '==', currentUser.uid), orderBy('name'));
      }
      const querySnapshot = await getDocs(clientsQuery);
      const clientsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(clientsData);
    } catch (error) {
      console.error("Błąd podczas pobierania klientów:", error);
      enqueueSnackbar('Nie udało się załadować listy klientów.', { variant: 'error' });
    }
  }, [currentUser, isAdmin, enqueueSnackbar]);

  useEffect(() => {
    if (currentUser) {
      fetchOrders();
      fetchClients();
    }
  }, [currentUser, fetchOrders, fetchClients]);

  useEffect(() => {
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin, fetchEmployees]);

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
    setFormData({
      client_name: '',
      client_phone: '',
      product_description: '',
      estimated_completion: '',
      deposit_amount: '',
      has_deposit: false,
      notes: '',
      status: 'new',
      total_amount: '',
      remaining_amount: '',
      is_fully_paid: false,
      is_delivered: false,
      create_sale_record: false,
      employee_id: ''
    });
    setSelectedClient(null);
  };

  const handleAddOrder = () => {
    setSelectedOrder(null);
    setOpenDialog(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    const client = clients.find(c => c.id === order.client_id);
    setSelectedClient(client || null);
    setFormData({
      ...order,
      estimated_completion: order.estimated_completion ? format(order.estimated_completion, 'yyyy-MM-dd') : '',
      has_deposit: !!order.deposit_amount,
      is_fully_paid: !!order.is_fully_paid,
      is_delivered: !!order.is_delivered,
      create_sale_record: !!order.create_sale_record,
    });
    setOpenDialog(true);
  };

  const calculateRemainingAmount = (total, deposit) => {
    const totalAmount = parseFloat(total) || 0;
    const depositAmount = parseFloat(deposit) || 0;
    return (totalAmount - depositAmount).toFixed(2);
  };

  const handleSubmit = async () => {
    if (!selectedClient) {
      enqueueSnackbar('Proszę wybrać klienta.', { variant: 'warning' });
      return;
    }

    let employeeDetails = {};
    if (selectedOrder) {
      employeeDetails = {
        employee_id: selectedOrder.employee_id,
        employee_name: selectedOrder.employee_name,
      };
    } else if (isAdmin && formData.employee_id) {
      const selectedEmployee = employees.find(emp => emp.id === formData.employee_id);
      employeeDetails = {
        employee_id: selectedEmployee?.id,
        employee_name: selectedEmployee?.name,
      };
    } else {
      employeeDetails = {
        employee_id: currentUser.uid,
        employee_name: currentUser.name,
      };
    }

    const orderData = {
      ...formData,
      client_id: selectedClient.id,
      client_name: selectedClient.name,
      client_phone: selectedClient.phone,
      total_amount: parseFloat(formData.total_amount) || 0,
      deposit_amount: formData.has_deposit ? (parseFloat(formData.deposit_amount) || 0) : 0,
      estimated_completion: formData.estimated_completion ? Timestamp.fromDate(new Date(formData.estimated_completion)) : null,
      ...employeeDetails,
    };

    try {
      if (selectedOrder) {
        const orderRef = doc(db, 'orders', selectedOrder.id);
        await updateDoc(orderRef, orderData);
        enqueueSnackbar('Zamówienie zaktualizowane pomyślnie!', { variant: 'success' });
        if (orderData.create_sale_record) {
          await createSaleRecord(orderData, selectedOrder.id);
        }
      } else {
        const docRef = await addDoc(collection(db, 'orders'), {
          ...orderData,
          created_at: Timestamp.now(),
        });
        enqueueSnackbar('Zamówienie dodane pomyślnie!', { variant: 'success' });
        if (orderData.create_sale_record) {
          await createSaleRecord(orderData, docRef.id);
        }
      }
      fetchOrders();
      handleDialogClose();
    } catch (error) {
      console.error("Błąd podczas zapisu zamówienia:", error);
      enqueueSnackbar('Wystąpił błąd podczas zapisu zamówienia.', { variant: 'error' });
    }
  };

  const createSaleRecord = async (orderData, orderId) => {
    try {
      const saleData = {
        client_id: orderData.client_id,
        client_name: orderData.client_name,
        products: [{
          id: `ORDER-${orderId}`,
          name: orderData.product_description,
          price: orderData.total_amount,
          quantity: 1,
          category: 'Zamówienie'
        }],
        total_amount: orderData.total_amount,
        payment_method: 'Przelew',
        payment_status: PAYMENT_STATUSES.PAID.value,
        delivery_status: DELIVERY_STATUSES.DELIVERED.value,
        sale_date: Timestamp.now(),
        employee_id: orderData.employee_id,
        employee_name: orderData.employee_name,
        notes: `Sprzedaż wygenerowana automatycznie z zamówienia #${orderId}. ${orderData.notes || ''}`,
        is_order_sale: true,
        order_id: orderId
      };

      const salesCollection = collection(db, 'sales_reports');
      const q = query(salesCollection, where('order_id', '==', orderId), limit(1));
      const existingSale = await getDocs(q);

      if (existingSale.empty) {
        await addDoc(salesCollection, saleData);
        enqueueSnackbar('Rekord sprzedaży został pomyślnie utworzony.', { variant: 'success' });
      } else {
        const saleDoc = existingSale.docs[0];
        await updateDoc(doc(db, 'sales_reports', saleDoc.id), saleData);
        enqueueSnackbar('Rekord sprzedaży został zaktualizowany.', { variant: 'info' });
      }
    } catch (error) {
      console.error("Błąd podczas tworzenia rekordu sprzedaży:", error);
      enqueueSnackbar('Nie udało się utworzyć rekordu sprzedaży.', { variant: 'error' });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Czy na pewno chcesz usunąć to zamówienie?')) {
      try {
        await deleteDoc(doc(db, 'orders', orderId));
        enqueueSnackbar('Zamówienie usunięte pomyślnie!', { variant: 'success' });
        fetchOrders();
      } catch (error) {
        console.error("Błąd podczas usuwania zamówienia:", error);
        enqueueSnackbar('Wystąpił błąd podczas usuwania zamówienia.', { variant: 'error' });
      }
    }
  };

  const handleDesignerOpen = (order) => {
    setSelectedOrder(order);
    setDesignerOpen(true);
  };

  const handleDesignerClose = () => {
    setDesignerOpen(false);
    setSelectedOrder(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const calculateFutureDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return format(date, 'yyyy-MM-dd');
  };

  const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" gutterBottom>
            Zamówienia w trakcie
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddOrder}
          >
            Dodaj zamówienie
          </Button>
        </Box>
        <FormControl fullWidth margin="normal">
          <InputLabel>Filtruj po pracowniku</InputLabel>
          <Select
            value={selectedEmployee}
            label="Filtruj po pracowniku"
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <MenuItem value=""><em>Wszyscy pracownicy</em></MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Klient</TableCell>
              <TableCell>Opis produktu</TableCell>
              <TableCell>Przewidywana realizacja</TableCell>
              <TableCell>Kwota</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Pracownik</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>{order.client_name}</TableCell>
                <TableCell>{order.product_description}</TableCell>
                <TableCell>
                  {order.estimated_completion ? format(new Date(order.estimated_completion), 'PPP', { locale: pl }) : 'Brak'}
                </TableCell>
                <TableCell>{typeof order.total_amount === 'number' ? `${order.total_amount.toFixed(2)} PLN` : 'B/D'}</TableCell>
                <TableCell>
                  <Chip 
                    label={ORDER_STATUS[order.status.toUpperCase()]?.label || order.status}
                    style={{
                      backgroundColor: ORDER_STATUS[order.status.toUpperCase()]?.color || '#808080',
                      color: 'white',
                    }}
                  />
                </TableCell>
                <TableCell>{order.employee_name || 'Brak'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {isAdmin && (
                      <Button variant="contained" size="small" onClick={() => handleDesignerOpen(order)}>Projektuj</Button>
                    )}
                    <Tooltip title="Edytuj">
                      <IconButton onClick={() => handleEditOrder(order)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Usuń">
                      <IconButton onClick={() => handleDeleteOrder(order.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={orders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zamówień na stronę:"
        />
      </TableContainer>

      {selectedOrder && (
        <Designer 
          open={designerOpen} 
          onClose={handleDesignerClose} 
          order={selectedOrder} 
        />
      )}

      <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
        <DialogTitle>{selectedOrder ? 'Edytuj zamówienie' : 'Dodaj nowe zamówienie'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Dane Klienta
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) => `${option.name} (${option.phone})`}
                value={selectedClient}
                onChange={handleClientChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => 
                  <TextField {...params} label="Wyszukaj lub wybierz klienta" />
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Imię i nazwisko klienta"
                name="client_name"
                value={formData.client_name}
                onChange={(e) => handleFormChange('client_name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefon klienta"
                name="client_phone"
                value={formData.client_phone}
                onChange={(e) => handleFormChange('client_phone', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Szczegóły Zamówienia
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Opis produktu"
                name="product_description"
                multiline
                rows={3}
                value={formData.product_description}
                onChange={(e) => handleFormChange('product_description', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Przewidywany czas realizacji (w dniach)"
                name="estimated_completion_days"
                type="number"
                onChange={(e) => {
                  const days = e.target.value;
                  const date = days ? new Date() : null;
                  if (date) date.setDate(date.getDate() + parseInt(days));
                  handleFormChange('estimated_completion', date ? date.toISOString().split('T')[0] : '');
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Przewidywana data realizacji"
                name="estimated_completion"
                type="date"
                value={formData.estimated_completion}
                onChange={(e) => handleFormChange('estimated_completion', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  {Object.values(ORDER_STATUS).map(status => (
                    <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isAdmin && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Przypisz pracownika</InputLabel>
                  <Select
                    value={formData.employee_id}
                    label="Przypisz pracownika"
                    onChange={(e) => handleFormChange('employee_id', e.target.value)}
                    required
                  >
                    {employees.map(emp => (
                      <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}


            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notatki do zamówienia"
                name="notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Informacje Finansowe
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kwota całkowita"
                name="total_amount"
                type="number"
                value={formData.total_amount}
                onChange={(e) => handleFormChange('total_amount', e.target.value)}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">PLN </InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.has_deposit}
                    onChange={(e) => handleFormChange('has_deposit', e.target.checked)}
                  />
                }
                label="Otrzymano zaliczkę"
              />
            </Grid>

            {formData.has_deposit && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kwota zaliczki"
                  name="deposit_amount"
                  type="number"
                  value={formData.deposit_amount}
                  onChange={(e) => handleFormChange('deposit_amount', e.target.value)}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">PLN </InputAdornment>,
                  }}
                />
              </Grid>
            )}
            
            {formData.has_deposit && formData.total_amount && formData.deposit_amount && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pozostało do zapłaty"
                  name="remaining_amount"
                  type="number"
                  value={formData.remaining_amount}
                  disabled
                  InputProps={{
                    startAdornment: <InputAdornment position="start">PLN </InputAdornment>,
                  }}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Status płatności i dostawy
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_fully_paid}
                    onChange={(e) => handleFormChange('is_fully_paid', e.target.checked)}
                  />
                }
                label="Pełna płatność otrzymana"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_delivered}
                    onChange={(e) => handleFormChange('is_delivered', e.target.checked)}
                  />
                }
                label="Produkt dostarczony/odebrany"
              />
            </Grid>
            
            {formData.is_fully_paid && formData.is_delivered && formData.status === 'completed' && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, mt: 1, bgcolor: '#f8f9fa' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.create_sale_record}
                        onChange={(e) => handleFormChange('create_sale_record', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SalesIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body1" fontWeight="bold">
                          Utwórz rekord sprzedaży w CRM
                        </Typography>
                      </Box>
                    }
                  />
                  
                  {formData.create_sale_record && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Po zapisaniu zamówienia zostanie automatycznie utworzony rekord sprzedaży w systemie CRM.
                    </Alert>
                  )}
                </Paper>
              </Grid>
            )}
            
            {!(formData.is_fully_paid && formData.is_delivered && formData.status === 'completed') && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Aby utworzyć rekord sprzedaży, zamówienie musi być oznaczone jako w pełni opłacone, dostarczone oraz mieć status "Zakończone".
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant="contained" startIcon={<ClearIcon />}>
            Anuluj
          </Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<CheckIcon />}>
            {selectedOrder ? 'Zapisz zmiany' : 'Dodaj zamówienie'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
